/**
 * NewFindsPage
 *
 * PR articles with a classifier score above 0.5 that have NOT yet been reviewed
 * for the PR topic (coversPRTopic has no value). One of the two views pulled out
 * of the old PR Bulk Review page into its own page.
 *
 * A "score page": it renders the shared minimum-PR-score slider above the
 * table. The page pins scorePRTopic > 0.5, so the slider's floor is 0.5 and the
 * PR-coverage filter is applied by the shared context for this sidebar option.
 */

import { useMemo } from "react"
import PrLiteratureReviewShell from "@/components/pr-literature-review/PrLiteratureReviewShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  usePrLiteratureReviewPage,
  stripLocationColumns,
} from "@/components/pr-literature-review/usePrLiteratureReviewPage"
import { getColumnGroupsForViewMode } from "@/components/pr-literature-review/columns"
import PrScoreSlider from "@/components/pr-literature-review/PrScoreSlider"

/** Browser tab title and page heading. */
const PAGE_TITLE: string = "New Finds"

/** Short description shown under the page heading. */
const PAGE_DESCRIPTION: string =
  "PR articles scored above 0.5 that have not yet been reviewed for the PR topic"

/**
 * The New Finds page.
 *
 * @returns The rendered page element.
 */
export default function NewFindsPage(): React.JSX.Element {
  const page = usePrLiteratureReviewPage({
    sidebarOption: "new-finds",
    // Highest-PR-score-first is the most useful default review order.
    defaultSort: { key: "scorePRTopic", direction: "desc" },
  })

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  return (
    <PrLiteratureReviewShell
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      <PrScoreSlider
        scoreMin={page.prScoreMin}
        scoreFloor={page.prScoreFloor}
        onScoreCommit={page.setPrScoreMin}
      />
      <JournalArticlesTable
        page={page}
        filterOptions={filterOptions}
        visibleColumns={visibleColumns}
        columnGroupsForViewMode={getColumnGroupsForViewMode}
      />
    </PrLiteratureReviewShell>
  )
}
